import { Zap, Home, Clock, Briefcase, Star, X, Image as ImageIcon, Tag } from "lucide-react";

/**
 * Chips rápidos para filtrar a lista com um toque.
 * - Grupo "Setor": chips de categoria (single-select, key="category").
 * - Grupo rápido: regime, modalidade, "há X dias", destaque, com foto.
 *
 * Props:
 *  - params: URLSearchParams
 *  - onToggle: (key, value) => void  (se já ativo, limpa a chave; caso contrário aplica)
 *  - facets: { category:[{value,count}], regime:[...], work_mode:[...] } — contagens por opção
 *  - categories: string[] — lista de categorias (de /meta) para os chips de setor
 */
export function QuickFilterChips({ params, onToggle, onClear, facets = {}, categories = [], className = "" }) {
  const quick = [
    { key: "regime", value: "Full-time", label: "Full-time", icon: Briefcase, facet: "regime" },
    { key: "regime", value: "Part-time", label: "Part-time", icon: Briefcase, facet: "regime" },
    { key: "work_mode", value: "Remoto", label: "Remoto", icon: Home, facet: "work_mode" },
    { key: "work_mode", value: "Híbrido", label: "Híbrido", icon: Home, facet: "work_mode" },
    { key: "days", value: "1", label: "Últimas 24h", icon: Clock },
    { key: "days", value: "7", label: "Últimos 7 dias", icon: Clock },
    { key: "featured", value: "true", label: "Em destaque", icon: Star },
    { key: "has_cover", value: "true", label: "Com foto", icon: ImageIcon },
  ];

  const catCountMap = {};
  (facets.category || []).forEach((f) => { catCountMap[f.value] = f.count; });
  // Ordenar setores pelos que têm mais vagas primeiro (sem contagem = 0, vão para o fim).
  const catChips = (categories || [])
    .map((c) => ({ key: "category", value: c, label: c, icon: Tag, facet: "category" }))
    .sort((a, b) => (catCountMap[b.value] || 0) - (catCountMap[a.value] || 0) || a.label.localeCompare(b.label));

  const allChips = [...catChips, ...quick];

  const countFor = (chip) => {
    if (!chip.facet) return undefined;
    const arr = facets[chip.facet];
    if (!Array.isArray(arr)) return undefined;
    const hit = arr.find((f) => f.value === chip.value);
    return hit ? hit.count : undefined;
  };

  const isActive = (chip) => String(params.get(chip.key) || "") === chip.value;
  const anyActive = allChips.some(isActive);

  const renderChip = (c) => {
    const active = isActive(c);
    const Icon = c.icon;
    const count = countFor(c);
    return (
      <button
        key={`${c.key}-${c.value}`}
        type="button"
        data-testid={`chip-${c.key}-${c.value.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => onToggle(c.key, active ? "" : c.value)}
        aria-pressed={active}
        className={`inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
          active
            ? c.key === "featured"
              ? "bg-amber-400 text-slate-900 border-amber-400 shadow-sm"
              : "bg-slate-900 text-white border-slate-900 shadow-sm"
            : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
        {c.label}
        {count != null && (
          <span
            className={`tabular-nums text-[10px] font-bold rounded-full px-1.5 py-px ${
              active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
            }`}
            data-testid={`chip-count-${c.key}-${c.value.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="quick-filter-chips">
      {catChips.length > 0 && (
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none] pb-0.5 sm:pb-0">
          <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mr-0.5 hidden sm:inline">
            Setor
          </span>
          {catChips.map(renderChip)}
        </div>
      )}
      <div className="flex items-center gap-2 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none] pb-0.5 sm:pb-0">
        {quick.map(renderChip)}
        {anyActive && (
          <button
            type="button"
            data-testid="chip-clear"
            onClick={() => {
              const keys = [...new Set(allChips.map((c) => c.key))];
              // Limpar TODAS as chaves de uma só vez (evita perder alterações
              // por partir sempre do mesmo snapshot de params num forEach).
              if (onClear) onClear(keys);
              else keys.forEach((k) => onToggle(k, ""));
            }}
            className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
          >
            <X className="w-3.5 h-3.5" /> Limpar
          </button>
        )}
      </div>
    </div>
  );
}

export default QuickFilterChips;
